import React, { useEffect, useState } from 'react';
import useBreakpoint from '../../hooks/useBreakpoint';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  UserPlus,
  Star,
  ShieldAlert,
  Cctv,
  Cpu,
  UserCircle2,
  ChevronLeft,
  Activity,
  ScrollText,
  CreditCard,
  Factory,
  Handshake,
  Receipt
} from 'lucide-react';

const AdminSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(true);
  const { isDesktop } = useBreakpoint();

  // Below desktop width the sidebar goes back to the rail when the viewport shrinks
  useEffect(() => {
    if (!isDesktop) setCollapsed(true);
  }, [isDesktop]);

  return (
    <div className={`absolute inset-y-0 left-0 z-40 lg:relative lg:z-auto flex h-dvh flex-col bg-[#1a1c23] text-gray-400 font-sans transition-all duration-300 shrink-0 ${collapsed ? 'w-20' : 'w-64 shadow-2xl lg:shadow-none'}`}>


      {/* Header del Sidebar */}
      <div className={`flex items-center py-6 text-white ${collapsed ? 'justify-center px-2' : 'justify-between px-6'}`}>
        {!collapsed && <h1 className="text-xl font-bold tracking-tight">Lisual Admin</h1>}
        <button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          className="p-2.5 rounded hover:bg-white/10 text-gray-500 hover:text-white transition-colors"
        >
          <ChevronLeft size={20} className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>


      <div className="px-4 mb-8">
        <div className={`flex items-center gap-3 px-2 py-3 rounded-lg hover:bg-white/5 cursor-pointer ${collapsed ? 'justify-center' : ''}`}>
          <UserCircle2 className="text-red-500 shrink-0" size={24} />
          {!collapsed && <span className="text-red-500 font-medium text-sm">ADMIN</span>}
        </div>
      </div>


      {/* Navegación Principal */}
      <nav className="flex-1 px-4 space-y-8 overflow-y-auto overflow-x-hidden">
        
        {/* Sección Clientes */}
        <div>
          {!collapsed && (
            <p className="px-2 mb-4 text-[11px] font-bold uppercase tracking-wider text-gray-500">Clientes</p>
          )}
          <ul className="space-y-1">

            <NavItem collapsed={collapsed} 
            icon={<LayoutDashboard size={20} />} 
            label="Dashboard Cliente" 
            onClick={() => navigate('/adminDashboard')}
            />

            <NavItem collapsed={collapsed}
            icon={<Factory size={20} />}
            label="Crear cliente"
            active={location.pathname.startsWith('/adminSales')}
            onClick={() => navigate('/adminSales')}
            />

            <NavItem collapsed={collapsed}
            icon={<Handshake size={20} />}
            label="Códigos de vendedores"
            onClick={() => navigate('/salesCodes')}
            />

            {/*<NavItem collapsed={collapsed} icon={<UserPlus size={20} />} label="Crear Cliente" />*/}

          </ul>
        </div>

        {/* Sección Proyectos oculta temporalmente
        <div>
          {!collapsed && (
            <p className="px-2 mb-4 text-[11px] font-bold uppercase tracking-wider text-gray-500">Proyectos</p>
          )}
          <ul className="space-y-1">
            <NavItem collapsed={collapsed} icon={<ShieldAlert size={20} />} label="Proyectos Pendientes" onClick={() => navigate('/pendingProjects')} />
          </ul>
        </div>
        */}



        {/* Sección Stocks */}
        <div>
          {!collapsed && (
            <p className="px-2 mb-4 text-[11px] font-bold uppercase tracking-wider text-gray-500">Stocks</p>
          )}
          <ul className="space-y-1">

            <NavItem collapsed={collapsed} 
                icon={<Cctv size={20} />} 
                label="Stock Camaras"
                onClick={() => navigate('/stockCams')}
            />

            <NavItem collapsed={collapsed} 
                icon={<Cpu size={20} />} 
                label="Stock Chips" 
                onClick={() => navigate('/stockChips')}
            />

          </ul>
        </div>

        {/* Sección Monitoreo */}
        <div>
          {!collapsed && (
            <p className="px-2 mb-4 text-[11px] font-bold uppercase tracking-wider text-gray-500">Monitoreo</p>
          )}
          <ul className="space-y-1">
            <NavItem collapsed={collapsed}
              icon={<Activity size={20} />}
              label="Monitor"
              active={location.pathname === '/adminMonitor'}
              onClick={() => navigate('/adminMonitor')}
            />
          </ul>
        </div>

        {/* Sección Logs */}
        <div>
          {!collapsed && (
            <p className="px-2 mb-4 text-[11px] font-bold uppercase tracking-wider text-gray-500">Logs</p>
          )}
          <ul className="space-y-1">
            <NavItem collapsed={collapsed}
              icon={<ScrollText size={20} />}
              label="Logs"
              onClick={() => navigate('/logs')}
            />
          </ul>
        </div>

        {/* Sección Cobranzas */}
        <div>
          {!collapsed && (
            <p className="px-2 mb-4 text-[11px] font-bold uppercase tracking-wider text-gray-500">Cobranzas</p>
          )}
          <ul className="space-y-1">
            <NavItem collapsed={collapsed}
              icon={<Receipt size={20} />}
              label="Cobranzas"
              active={location.pathname.startsWith('/cobranzas')}
              onClick={() => navigate('/cobranzas')}
            />
          </ul>
        </div>

        {/* Sección Licencias */}
        <div>
          {!collapsed && (
            <p className="px-2 mb-4 text-[11px] font-bold uppercase tracking-wider text-gray-500">Licencias</p>
          )}
          <ul className="space-y-1">
            <NavItem collapsed={collapsed}
              icon={<CreditCard size={20} />}
              label="Licencias"
              onClick={() => navigate('/licencias')}
            />
          </ul>
        </div>

      </nav>
    </div>
  );
};

// Sub-componente para los items del menú
const NavItem = ({ icon, label, active = false, onClick, collapsed = false }) => {
  return (
    <li className={`
      flex items-center gap-4 px-3 py-3 rounded-md cursor-pointer transition-colors
      ${collapsed ? 'justify-center' : ''}
      ${active
        ? 'bg-[#2563eb] text-white shadow-lg'
        : 'hover:bg-white/5 hover:text-gray-200'}
    `}
    onClick={onClick}
    title={collapsed ? label : undefined}>
      <span className={active ? "text-white" : "text-gray-400"}>
        {icon}
      </span>
      {!collapsed && <span className="text-sm font-medium whitespace-nowrap">{label}</span>}
    </li>
  );
};

export default AdminSidebar;